/**
 * Admin-app per-page ErrorBoundary (W0.8).
 *
 * Friendlier than the global `ErrorBoundary`. When a single page render
 * fails, we keep the staff inside the admin shell and offer a recovery
 * path so the rest of the dashboard stays usable.
 */

import { Component, type ErrorInfo, type ReactNode } from 'react';
import { Card } from '@ferro/ui';

interface PageErrorBoundaryProps {
  readonly children: ReactNode;
  readonly title?: string;
  readonly onError?: (error: Error, info: ErrorInfo) => void;
}

interface PageErrorBoundaryState {
  readonly hasError: boolean;
  readonly error: Error | null;
}

export class PageErrorBoundary extends Component<
  PageErrorBoundaryProps,
  PageErrorBoundaryState
> {
  constructor(props: PageErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): PageErrorBoundaryState {
    return { hasError: true, error };
  }

  override componentDidCatch(error: Error, info: ErrorInfo): void {
    // eslint-disable-next-line no-console
    console.error('[admin] PageErrorBoundary caught:', error, info.componentStack);
    this.props.onError?.(error, info);
  }

  private readonly handleRetry = (): void => {
    this.setState({ hasError: false, error: null });
  };

  private readonly handleGoDashboard = (): void => {
    this.setState({ hasError: false, error: null });
    if (typeof window !== 'undefined') {
      window.location.href = '/';
    }
  };

  override render(): ReactNode {
    if (!this.state.hasError) {
      return this.props.children;
    }

    return (
      <section
        className="ferro-admin-page ferro-page-error"
        role="alert"
        aria-live="assertive"
      >
        <Card title={this.props.title ?? 'This page hit a problem'}>
          <p className="ferro-muted">
            We hit an unexpected issue loading this page. You can retry, or
            head back to the dashboard.
          </p>
          <div className="ferro-page-error__actions">
            <button
              type="button"
              className="ferro-btn ferro-btn--primary"
              onClick={this.handleRetry}
            >
              Try again
            </button>
            <button
              type="button"
              className="ferro-btn ferro-btn--ghost"
              onClick={this.handleGoDashboard}
            >
              Back to dashboard
            </button>
          </div>
        </Card>
      </section>
    );
  }
}
